import {
  HalfFloatType,
  type Camera,
  type Scene,
  type WebGLRenderer,
} from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { Vector2 } from 'three';
import type { PostProcessConfig } from './types';

export class PostProcessing {
  private composer: EffectComposer;
  private fxaaPass: ShaderPass | null = null;

  constructor(renderer: WebGLRenderer, scene: Scene, camera: Camera, config: PostProcessConfig) {
    const size = renderer.getSize(new Vector2());
    const pixelRatio = renderer.getPixelRatio();

    this.composer = new EffectComposer(renderer);
    this.composer.renderTarget1.texture.type = HalfFloatType;
    this.composer.renderTarget2.texture.type = HalfFloatType;

    const renderPass = new RenderPass(scene, camera);
    this.composer.addPass(renderPass);

    if (config.bloom) {
      const bloomPass = new UnrealBloomPass(
        new Vector2(size.x, size.y),
        config.bloom.strength,
        config.bloom.radius,
        config.bloom.threshold,
      );
      this.composer.addPass(bloomPass);
    }

    if (config.fxaa !== false) {
      this.fxaaPass = new ShaderPass(FXAAShader);
      this.fxaaPass.uniforms['resolution'].value.set(
        1 / (size.x * pixelRatio),
        1 / (size.y * pixelRatio),
      );
      this.composer.addPass(this.fxaaPass);
    }

    const outputPass = new OutputPass();
    this.composer.addPass(outputPass);
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.composer.setSize(width, height);
    if (this.fxaaPass) {
      this.fxaaPass.uniforms['resolution'].value.set(
        1 / (width * pixelRatio),
        1 / (height * pixelRatio),
      );
    }
  }

  render(): void {
    this.composer.render();
  }

  dispose(): void {
    this.composer.dispose();
  }
}
